
import { defineComponent, reactive, toRefs, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import moment, { Moment, unitOfTime, MomentInput } from 'moment'
import TimeSeeker from '@/components/rain_detail/TimeSeeker.vue'
import UdcBarGraph from '@/components/common/UdcBarGraph.vue'
import { RESOLUTIONS, Resolution, TIME_UNITS } from '@/lib/constants'
import { nowFor10Min, now } from '@/lib/time'
import { useHistoryStore } from '@/stores/historyStore'
import { HistoryItem } from '@/models/History'
import { useGlobalStore } from '@/stores/globalStore'
import { rangeToISO } from '@/lib/formatter'

export type GraphPoint = {
  statusTime?: string
  rainfall: number
}

const NUMBER_OF_POINTS_MIN = 47 // グラフに表示する棒の数(10分)
const NUMBER_OF_POINTS = 14 // グラフに表示する棒の数(時・日)

const GRAPH_SPANS_MIN = {
  [RESOLUTIONS.TEN_MIN]: NUMBER_OF_POINTS_MIN * 10,
  [RESOLUTIONS.HOURLY]: NUMBER_OF_POINTS_MIN * 60,
  [RESOLUTIONS.DAILY]: NUMBER_OF_POINTS_MIN * 60 * 24,
}

const GRAPH_SPANS = {
  [RESOLUTIONS.TEN_MIN]: NUMBER_OF_POINTS * 10,
  [RESOLUTIONS.HOURLY]: NUMBER_OF_POINTS * 60,
  [RESOLUTIONS.DAILY]: NUMBER_OF_POINTS * 60 * 24,
}

const GRAPH_TITLES = {
  [RESOLUTIONS.TEN_MIN]: '10分間移動積算雨量',
  [RESOLUTIONS.HOURLY]: '1時間移動積算雨量',
  [RESOLUTIONS.DAILY]: '日積算雨量',
}

const RESOLUTION_TYPE_MAP = {
  [RESOLUTIONS.TEN_MIN]: 'hour',
  [RESOLUTIONS.HOURLY]: 'day',
  [RESOLUTIONS.DAILY]: 'month',
}

const SPANS = [
  { label: '10分間', value: RESOLUTIONS.TEN_MIN },
  { label: '1時間', value: RESOLUTIONS.HOURLY },
  { label: '日積算', value: RESOLUTIONS.DAILY },
]

type State = {
  resolution: Resolution
  graphDatasets: GraphPoint[]
  statusTime: Moment
}

export default defineComponent({
  components: {
    TimeSeeker,
    UdcBarGraph,
  },

  setup() {
    const historyStore = useHistoryStore()
    const globalStore = useGlobalStore()

    const state = reactive<State>({
      resolution: RESOLUTIONS.TEN_MIN,
      statusTime: nowFor10Min(),
      graphDatasets: [],
    })
    const graphTitle = computed(() => GRAPH_TITLES[state.resolution])
    const thresholds = computed(() => {
      const target =
        state.resolution === 'daily' ? 'rui' : state.resolution === '10min' ? 'move_rain_min_10' : 'move_rain_min_60'

      return globalStore.rainThreshold.filter(t => t.target === target)
    })
    const currentDateLabel = computed(() => {
      const lastData = state.graphDatasets.slice(-1)[0]
      const dateStr = lastData?.statusTime as MomentInput
      const mDate = moment(dateStr)

      return state.resolution === 'daily' ? '' : mDate.format('MM/DD')
    })

    const getValue = (item: HistoryItem) => {
      if (state.resolution === RESOLUTIONS.TEN_MIN) {
        return item.moveRainMin10 || 0
      } else if (state.resolution === RESOLUTIONS.HOURLY) {
        return item.moveRainMin60 || 0
      } else {
        return item.rainDay || 0
      }
    }

    const route = useRoute()
    const fetchGraphHistoryData = async () => {
      const numberofPoints = state.resolution === RESOLUTIONS.TEN_MIN ? 48 : 16
      const resolution = state.resolution
      const statusTime = state.statusTime
      const period = state.resolution === RESOLUTIONS.TEN_MIN ? GRAPH_SPANS_MIN[resolution] : GRAPH_SPANS[resolution]
      const type = RESOLUTION_TYPE_MAP[resolution]
      const endTime = moment(statusTime).format('YYYY-MM-DD HH:mm:ss')
      const startTime = moment(statusTime).add(-period, 'minutes').format('YYYY-MM-DD HH:mm:ss')
      const stationId = globalStore.currentStation?.stationId || (route.params.stationId as string)

      const params = {
        stationId,
        range: {
          ...rangeToISO({ startTime, endTime } as Range),
          type,
        },
        target: ['move_rain_min_10', 'move_rain_min_60', 'rain_day'],
      }

      const response = await historyStore.fetch(params)
      const data = response.items
      const span = resolution === RESOLUTIONS.TEN_MIN ? 10 : resolution === RESOLUTIONS.HOURLY ? 60 : 60 * 24
      const results = [] as GraphPoint[]

      Array.from(Array(numberofPoints - 1)).map((_, index) => {
        const targetTime = moment(statusTime)
          .add(-period, 'minutes')
          .add(index * span, 'minutes')
        const candidate = data.find(datum => {
          const unit = TIME_UNITS[resolution] as unitOfTime.StartOf

          return moment(datum.time).isSame(targetTime, unit)
        })
        let tmpTime: Moment = candidate ? moment(candidate.time) : targetTime

        if (resolution === 'daily') {
          tmpTime = tmpTime.add(-1, 'day')
        }

        const result = candidate
          ? {
              statusTime: tmpTime.format(),
              rainfall: getValue(candidate),
            }
          : { statusTime: tmpTime.format(), rainfall: 0 }

        results.push(result)
      })

      state.graphDatasets = results
    }
    fetchGraphHistoryData()

    const defaultStatusTime = () => {
      const nowTime = now(state.resolution)

      if (state.resolution !== 'daily') {
        return nowTime
      }

      return nowTime.add(1, 'day')
    }

    watch(
      () => state.statusTime,
      () => {
        fetchGraphHistoryData()
      }
    )

    watch(
      () => globalStore.currentStation,
      () => {
        state.statusTime = defaultStatusTime()

        fetchGraphHistoryData()
      }
    )

    watch(
      () => state.resolution,
      () => {
        state.statusTime = defaultStatusTime()

        fetchGraphHistoryData()
      }
    )

    return {
      ...toRefs(state),
      graphTitle,
      spans: SPANS,
      thresholds,
      currentDateLabel,
    }
  },
})
